import { useCallback, useMemo } from 'react';
import { Transaction } from '../../../../../Overview/types';
import './index.css';
import { VictoryCandlestick, VictoryGroup, VictoryScatter } from 'victory';
import { colors } from '../../../../../../colors';
import { CompactTransaction } from '../../../../../ProfitCalculations/types';

interface Props {
  transaction: Transaction;
  compactTransactions: CompactTransaction[];
}

const TransactionItemCandlesChart = (props: Props) => {
  const { transaction, compactTransactions } = props;

  const candleDataPoints = useMemo(() => {
    if (
      typeof transaction.candles === 'undefined' ||
      transaction.candles?.higher.length === 0
    ) {
      return [];
    }

    const dataPoints = transaction.candles.higher.map((c) => {
      return {
        ...c,
        x: c.startTimestamp,
      };
    });

    return dataPoints;
  }, [transaction.candles]);

  const findClosestCandleTimestamp = useCallback(
    (timestamp: number) => {
      if (!transaction.candles) {
        return timestamp;
      }

      const { timestamp: nearbyTimestamp } = transaction.candles.higher.reduce(
        (acc, curr) => {
          const diff = Math.abs(timestamp - curr.startTimestamp);

          if (diff < acc.min && curr.startTimestamp <= timestamp) {
            acc.min = diff;
            acc.timestamp = curr.startTimestamp;
          }

          return acc;
        },
        { min: Infinity, timestamp: 0 },
      );

      return nearbyTimestamp ?? timestamp;
    },
    [transaction.candles],
  );

  const transactionDataPoints = useMemo(() => {
    return compactTransactions
      .filter(
        (t, i, s) =>
          s.findIndex(
            (ts) => ts.compactTransactionId === t.compactTransactionId,
          ) === i,
      )

      .map((t) => ({
        x: findClosestCandleTimestamp(
          t.timestamp || t.transactionTimestamp || 0,
        ),
        y: t.orderPrice,
        color: t.orderType === 'BUY' ? colors.green : colors.red,
        strokeColor: t.transactionType.includes('TAKE_PROFIT')
          ? colors.blue
          : colors.white,
      }));
  }, [compactTransactions, findClosestCandleTimestamp]);

  const getCandleStrokeColor = useCallback(
    (c: any) => (c.datum.close >= c.datum.open ? 'green' : 'red'),
    [],
  );

  if (!transaction.candles || transaction.candles.higher.length === 0) {
    return null;
  }

  return (
    <div className="transaction-item-candles-root">
      <VictoryGroup domainPadding={0} padding={5} colorScale={undefined}>
        <VictoryCandlestick
          data={candleDataPoints}
          candleColors={{ positive: colors.green, negative: colors.red }}
          style={{
            data: {
              stroke: getCandleStrokeColor,
              strokeWidth: 0.4,
              fill: getCandleStrokeColor,
            },
          }}
        />
        <VictoryScatter
          data={transactionDataPoints}
          style={{
            data: {
              fill: ({ datum }) => datum.color,
              stroke: (d) => d.datum.strokeColor,
              strokeWidth: 2,
            },
          }}
          size={8}
        />
      </VictoryGroup>
    </div>
  );
};

export default TransactionItemCandlesChart;
